import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Languages, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { useNetwork } from '@/contexts/NetworkContext'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { apiErrorDescription } from '@/services/core/apiError'
import { newReportId } from '@/services/reports/reportsApi'
import type { ListSort } from '@/services/core/paging'
import {
  ReportTranslationsApi,
  type ReportTranslation,
} from '@/services/reports/reportTranslationsApi'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { SearchInput } from '../../components/SearchInput'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ReportTranslationTable } from '../../components/reports/ReportTranslationTable'

export function ReportTranslationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { scopeKey, scopeReady } = useNetwork()
  const [search, setSearch] = useUrlSearch()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.reportTranslations.status',
    'ACTIVE'
  )
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<ReportTranslation[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['report-translations', 'page', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () =>
      ReportTranslationsApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const translations = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((tr) => ReportTranslationsApi.update(tr.id, { name: tr.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['report-translations'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE'
            ? 'reportTranslations.doneDetail_activate'
            : 'reportTranslations.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('reportTranslations.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('reportTranslations.title')}</h1>
            <p>{t('reportTranslations.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => navigate(`/reports/translations/${newReportId()}?new=true`)}>
              <Plus className="size-4" /> {t('reportTranslations.newTranslation')}
            </Button>
          </div>
        </div>

        <ListCard tone="insight">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Languages} />
                {t('reportTranslations.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              {
                label: t('common.activate'),
                icon: 'pi pi-check-circle',
                severity: 'success',
                onClick: () => bulkSetStatus('ACTIVE'),
              },
              {
                label: t('common.deactivate'),
                icon: 'pi pi-ban',
                severity: 'danger',
                onClick: () => bulkSetStatus('INACTIVE'),
              },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <ReportTranslationTable
              translations={translations}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('reportTranslations.translationCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
